import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CharacteristicConsultService } from 'src/app/pages/register/characteristic-consult/services/characteristic-consult.service';
import { CarbonCredit } from 'src/app/shared/interfaces/carbon-credit';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';

import {
  OperationTypeBadge,
  resolveOperationTypeBadge,
} from '../../operation-list/shared/operation-type-badge.utils';
import {
  displayValue,
  formatComitenteDocument,
  isExternalUrl,
} from '../../../register/register-confirmation/shared/register-confirmation-details.utils';

@Component({
  standalone: false,
  selector: 'app-operation-confirmation-details-drawer',
  templateUrl: './operation-confirmation-details-drawer.component.html',
  styleUrls: ['./operation-confirmation-details-drawer.component.scss'],
})
export class OperationConfirmationDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() operation: CarbonOperation | null = null;

  @Output() closed = new EventEmitter<void>();

  credit: CarbonCredit | null = null;
  isLoading = false;
  loadError: string | null = null;

  private loadSubscription?: Subscription;

  constructor(
    private readonly characteristicConsultService: CharacteristicConsultService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true || changes['operation']) {
      this.loadDetailsIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  get typeBadge(): OperationTypeBadge {
    return this.operation
      ? resolveOperationTypeBadge(this.operation)
      : { label: 'Depósito', icon: 'check_circle', tone: 'success' };
  }

  get issueDate(): string {
    return displayValue(this.credit?.issueDate);
  }

  get comitenteDocument(): string {
    return formatComitenteDocument(this.credit?.comitenteDocumentNumber);
  }

  get issuedQuantity(): string {
    return displayValue(this.credit?.creditInitialQuantity);
  }

  get currentQuantity(): string {
    return displayValue(this.credit?.creditQuantity);
  }

  get vintageStart(): string {
    return displayValue(this.credit?.vintageStart);
  }

  get vintageEnd(): string {
    return displayValue(this.credit?.vintageEnd);
  }

  get serialRange(): string {
    return displayValue(this.credit?.serialCode);
  }

  get projectLink(): string {
    return displayValue(this.credit?.projectLink);
  }

  get certifierName(): string {
    return displayValue(this.credit?.certifierDescription);
  }

  get additionalDescription(): string {
    return displayValue(this.credit?.observationText);
  }

  get hasProjectLink(): boolean {
    return isExternalUrl(this.credit?.projectLink);
  }

  get showBrcerBadge(): boolean {
    return this.credit?.brcerIndicator === true;
  }

  close(): void {
    this.closed.emit();
  }

  private loadDetailsIfNeeded(): void {
    const carbonCreditCode = this.operation?.carbonCreditCode?.trim();

    if (!this.isOpen || !carbonCreditCode) {
      return;
    }

    this.isLoading = true;
    this.loadError = null;
    this.credit = null;

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.characteristicConsultService
      .getCarbonCreditById(carbonCreditCode)
      .subscribe({
        next: credit => {
          this.credit = credit;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.loadError = 'Não foi possível carregar os detalhes do crédito.';
        },
      });
  }
}
